<template>
  <v-main>
    <v-container fluid>

      <!-- Page Header -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex align-center">
            <v-btn fab x-small color="primary" class="mr-3" @click="$router.back()">
              <v-icon dark>mdi-arrow-left</v-icon>
            </v-btn>
            <div>
              <h2 class="font-weight-bold" style="color: #1a1a2e;">Edit Gift Catalogs</h2>
              <span class="text-caption grey--text">Manage gift catalog items and their display order</span>
            </div>
            <v-spacer></v-spacer>
            <template v-if="activeTab === 0">
              <v-btn color="secondary" outlined rounded class="mr-2" @click="addLevel">
                <v-icon left>mdi-plus</v-icon>
                Add Level
              </v-btn>
              <v-btn
                color="primary"
                rounded
                :loading="isLoading"
                :disabled="isLoading"
                @click="save"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save Changes
              </v-btn>
            </template>
          </div>
        </v-col>
      </v-row>

      <!-- Tabs -->
      <v-tabs v-model="activeTab" background-color="transparent" class="mb-4" @change="onTabChange">
        <v-tab>
          <v-icon left small>mdi-gift</v-icon>
          Active Items
          <v-chip small class="ml-2" color="primary" text-color="white">{{ form.giftCatalog.length }}</v-chip>
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-delete-clock</v-icon>
          Deleted Items
          <v-chip small class="ml-2" color="red" text-color="white" v-if="deletedItems.length > 0">{{ deletedItems.length }}</v-chip>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">

        <!-- Tab 1: Active Items -->
        <v-tab-item>
          <v-row>
            <v-col
              v-for="(level, index) in form.giftCatalog"
              :key="level._uid"
              cols="12"
            >
              <v-card elevation="4" rounded="xl" class="overflow-hidden">

                <!-- Card Header -->
                <div
                  class="d-flex align-center px-5 py-3"
                  style="background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);"
                >
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="mr-3">
                      <span class="white--text font-weight-bold text-body-2">{{ index + 1 }}</span>
                    </v-avatar>
                    <div>
                      <span class="white--text font-weight-bold">Level {{ index + 1 }}</span>
                      <span class="ml-2 grey--text text--lighten-1 text-caption">{{ level.name || 'Unnamed Item' }}</span>
                    </div>
                  </div>

                  <v-spacer></v-spacer>

                  <!-- Order Controls -->
                  <div class="d-flex align-center mr-4">
                    <v-chip
                      small
                      color="white"
                      text-color="#1a1a2e"
                      class="mr-3 font-weight-bold"
                      style="border: 2px solid #e60040; min-width: 80px; justify-content: center;"
                    >
                      <v-icon left small color="#e60040">mdi-sort-numeric-ascending</v-icon>
                      Order {{ index + 1 }}
                    </v-chip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs" v-on="on"
                          icon small
                          :disabled="index === 0"
                          color="white"
                          class="mr-1"
                          @click="moveUp(index)"
                        >
                          <v-icon small>mdi-chevron-up</v-icon>
                        </v-btn>
                      </template>
                      <span>Move Up</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs" v-on="on"
                          icon small
                          :disabled="index === form.giftCatalog.length - 1"
                          color="white"
                          @click="moveDown(index)"
                        >
                          <v-icon small>mdi-chevron-down</v-icon>
                        </v-btn>
                      </template>
                      <span>Move Down</span>
                    </v-tooltip>
                  </div>

                  <!-- Delete -->
                  <v-tooltip bottom v-if="form.giftCatalog.length > 1">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small color="red lighten-2" @click="removeLevel(index)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Remove Level</span>
                  </v-tooltip>
                </div>

                <!-- Card Body -->
                <v-card-text class="pa-5">
                  <v-row>

                    <!-- Left: Image Section -->
                    <v-col cols="12" sm="3">
                      <div
                        class="image-upload-area d-flex flex-column align-center justify-center rounded-lg"
                        style="border: 2px dashed #e0e0e0; min-height: 200px; background: #fafafa; position: relative; overflow: hidden;"
                      >
                        <img
                          v-if="getImageSrc(level)"
                          :src="getImageSrc(level)"
                          style="width: 100%; height: 200px; object-fit: cover; border-radius: 8px;"
                        />
                        <div v-else class="text-center pa-4">
                          <v-icon size="48" color="grey lighten-2">mdi-image-plus</v-icon>
                          <p class="text-caption grey--text mt-2 mb-0">Click below to upload image</p>
                        </div>
                      </div>

                      <!-- Upload Controls -->
                      <div class="mt-2 d-flex align-center">
                        <v-btn
                          small
                          outlined
                          color="primary"
                          rounded
                          class="flex-grow-1 mr-1"
                          @click="$refs['file_' + index][0].click()"
                        >
                          <v-icon left x-small>mdi-upload</v-icon>
                          Upload
                        </v-btn>
                        <v-btn
                          small
                          outlined
                          color="red"
                          icon
                          @click="removeImage(index)"
                        >
                          <v-icon x-small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <input
                        type="file"
                        :ref="'file_' + index"
                        @change="onFileChange($event, index)"
                        accept="image/*"
                        style="display: none;"
                      />
                    </v-col>

                    <!-- Right: Form Fields -->
                    <v-col cols="12" sm="9">
                      <v-row dense>

                        <!-- Name -->
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model="level.name"
                            label="Gift Name"
                            outlined
                            dense
                            prepend-inner-icon="mdi-gift"
                            :error-messages="formErrors[`giftCatalog.${index}.name`] || []"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>

                        <!-- Points -->
                        <v-col cols="12" sm="3">
                          <v-text-field
                            v-model="level.points"
                            label="Points"
                            outlined
                            dense
                            prepend-inner-icon="mdi-star"
                            type="number"
                            :error-messages="formErrors[`giftCatalog.${index}.points`] || []"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>

                        <!-- Amount -->
                        <v-col cols="12" sm="3">
                          <v-text-field
                            v-model="level.amount"
                            label="Amount (₹)"
                            outlined
                            dense
                            prepend-inner-icon="mdi-currency-inr"
                            type="number"
                            step="0.01"
                            min="0"
                            :error-messages="formErrors[`giftCatalog.${index}.amount`] || []"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>

                        <!-- HSN Code -->
                        <v-col cols="12" sm="3">
                          <v-text-field
                            v-model="level.hsn_code"
                            label="HSN Code"
                            outlined
                            dense
                            prepend-inner-icon="mdi-barcode"
                            maxlength="20"
                            :error-messages="formErrors[`giftCatalog.${index}.hsn_code`] || []"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>

                        <!-- Dimensions Subheading -->
                        <v-col cols="12" class="pb-0 pt-3">
                          <div class="d-flex align-center">
                            <v-icon small color="grey" class="mr-1">mdi-package-variant</v-icon>
                            <span class="text-caption font-weight-bold grey--text text--darken-1 text-uppercase letter-spacing-wide">Shipping Dimensions</span>
                            <v-divider class="ml-2"></v-divider>
                          </div>
                        </v-col>

                        <!-- Width -->
                        <v-col cols="6" sm="3">
                          <v-text-field
                            v-model="level.width"
                            label="Width (cm)"
                            outlined
                            dense
                            type="number"
                            step="0.01"
                            min="0"
                            suffix="cm"
                            :error-messages="formErrors[`giftCatalog.${index}.width`] || []"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>

                        <!-- Height -->
                        <v-col cols="6" sm="3">
                          <v-text-field
                            v-model="level.height"
                            label="Height (cm)"
                            outlined
                            dense
                            type="number"
                            step="0.01"
                            min="0"
                            suffix="cm"
                            :error-messages="formErrors[`giftCatalog.${index}.height`] || []"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>

                        <!-- Length -->
                        <v-col cols="6" sm="3">
                          <v-text-field
                            v-model="level.length"
                            label="Length (cm)"
                            outlined
                            dense
                            type="number"
                            step="0.01"
                            min="0"
                            suffix="cm"
                            :error-messages="formErrors[`giftCatalog.${index}.length`] || []"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>

                        <!-- Weight -->
                        <v-col cols="6" sm="3">
                          <v-text-field
                            v-model="level.weight"
                            label="Weight (g)"
                            outlined
                            dense
                            type="number"
                            step="0.001"
                            min="0"
                            suffix="g"
                            :error-messages="formErrors[`giftCatalog.${index}.weight`] || []"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>

                      </v-row>
                    </v-col>

                  </v-row>
                </v-card-text>

              </v-card>
            </v-col>
          </v-row>

          <!-- Bottom Actions -->
          <v-row class="mt-2" v-if="!isLoading">
            <v-col cols="12" class="d-flex justify-end">
              <v-btn color="secondary" outlined rounded class="mr-2" @click="addLevel">
                <v-icon left>mdi-plus</v-icon>
                Add Level
              </v-btn>
              <v-btn
                color="primary"
                rounded
                @click="save"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save Changes
              </v-btn>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Tab 2: Deleted Items -->
        <v-tab-item>
          <v-row v-if="isLoadingDeleted" class="justify-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-row>
          <v-row v-else-if="deletedItems.length === 0">
            <v-col cols="12">
              <v-card elevation="2" rounded="xl" class="pa-8 text-center">
                <v-icon size="64" color="grey lighten-2">mdi-delete-empty</v-icon>
                <p class="text-h6 grey--text mt-4 mb-0">No deleted items</p>
                <p class="text-caption grey--text">Items removed from the catalog will appear here</p>
              </v-card>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col
              v-for="item in deletedItems"
              :key="'deleted-' + item.id"
              cols="12"
            >
              <v-card elevation="2" rounded="xl" class="overflow-hidden" style="border: 2px dashed #e60040;">

                <!-- Card Header -->
                <div
                  class="d-flex align-center px-5 py-3"
                  style="background: linear-gradient(135deg, #5a1a1a 0%, #3e1616 100%);"
                >
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="red" class="mr-3">
                      <v-icon small color="white">mdi-delete</v-icon>
                    </v-avatar>
                    <div>
                      <span class="white--text font-weight-bold">{{ item.name || 'Unnamed Item' }}</span>
                      <v-chip v-if="item.has_orders" x-small color="orange" text-color="white" class="ml-2">
                        <v-icon left x-small>mdi-truck-delivery</v-icon>
                        Has Delivered Orders
                      </v-chip>
                    </div>
                  </div>

                  <v-spacer></v-spacer>

                  <div class="d-flex align-center">
                    <v-chip small color="white" text-color="grey" class="mr-3">
                      <v-icon left small>mdi-star</v-icon>
                      {{ item.points }} pts
                    </v-chip>
                    <v-btn
                      small
                      rounded
                      color="green"
                      dark
                      :loading="restoringId === item.id"
                      @click="restoreItem(item)"
                    >
                      <v-icon left small>mdi-restore</v-icon>
                      Restore
                    </v-btn>
                  </div>
                </div>

                <!-- Card Body -->
                <v-card-text class="pa-4">
                  <v-row align="center">
                    <v-col cols="12" sm="2" v-if="item.image_path">
                      <img
                        :src="mediaUrl + item.image_path"
                        style="width: 100%; max-height: 120px; object-fit: cover; border-radius: 8px;"
                      />
                    </v-col>
                    <v-col>
                      <v-row dense>
                        <v-col cols="auto">
                          <span class="text-caption grey--text">Gift Name:</span>
                          <span class="ml-1 font-weight-medium">{{ item.name }}</span>
                        </v-col>
                        <v-col cols="auto">
                          <span class="text-caption grey--text">Points:</span>
                          <span class="ml-1 font-weight-medium">{{ item.points }}</span>
                        </v-col>
                        <v-col cols="auto" v-if="item.amount">
                          <span class="text-caption grey--text">Amount:</span>
                          <span class="ml-1 font-weight-medium">₹{{ item.amount }}</span>
                        </v-col>
                        <v-col cols="auto" v-if="item.hsn_code">
                          <span class="text-caption grey--text">HSN:</span>
                          <span class="ml-1 font-weight-medium">{{ item.hsn_code }}</span>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>

              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

      </v-tabs-items>

    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "GiftCatalogIdPage",
  data() {
    return {
      activeTab: 0,
      filter: {
        positionId: null,
        is_active: 1,
      },
      form: {
        giftCatalog: [],
      },
      deletedItems: [],
      restoringId: null,
      isLoadingDeleted: false,
      formErrors: {},
      isLoading: false,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    getImageSrc(level) {
      if (!level.image_path) return null;
      if (level.image_path instanceof File) {
        return URL.createObjectURL(level.image_path);
      }
      return this.mediaUrl + level.image_path;
    },
    moveUp(index) {
      if (index === 0) return;
      const arr = this.form.giftCatalog;
      const temp = arr[index - 1];
      this.$set(arr, index - 1, arr[index]);
      this.$set(arr, index, temp);
    },
    moveDown(index) {
      const arr = this.form.giftCatalog;
      if (index === arr.length - 1) return;
      const temp = arr[index + 1];
      this.$set(arr, index + 1, arr[index]);
      this.$set(arr, index, temp);
    },
    addLevel() {
      this.form.giftCatalog.push({
        _uid: 'new_' + Date.now(),
        name: null,
        image_path: null,
        points: null,
        amount: null,
        hsn_code: null,
        width: null,
        height: null,
        length: null,
        weight: null,
        sort_order: this.form.giftCatalog.length,
      });
    },
    removeLevel(index) {
      this.form.giftCatalog.splice(index, 1);
    },
    onFileChange(event, index) {
      const file = event.target.files[0];
      if (file) {
        this.$set(this.form.giftCatalog[index], 'image_path', file);
      }
    },
    removeImage(index) {
      const ref = this.$refs["file_" + index];
      if (ref && ref[0]) ref[0].value = null;
      this.$set(this.form.giftCatalog[index], 'image_path', null);
    },
    async getData() {
      this.isLoading = true;
      if (this.$route.params.positionId) {
        this.filter.position_id = this.decryptIt(this.$route.params.positionId);
      } else {
        this.filter.position_id = "";
      }
      this.filter.is_active = this.filter.is_active ? this.filter.is_active : "";
      let query =
        "position_id=" + this.filter.position_id +
        "&is_active=" + this.filter.is_active;
      await axios
        .get(`gift_catalogs?${query}`)
        .then((response) => {
          this.form.giftCatalog = response.data.data.map(item => ({ ...item, _uid: item.id }));
          this.count = response.data.count;
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
    onTabChange(tab) {
      if (tab === 1) {
        this.getDeletedItems();
      }
    },
    async getDeletedItems() {
      this.isLoadingDeleted = true;
      let positionId = "";
      if (this.$route.params.positionId) {
        positionId = this.decryptIt(this.$route.params.positionId);
      }
      try {
        const response = await axios.get(`/gift_catalogs/deleted?position_id=${positionId}`);
        this.deletedItems = response.data.data;
      } catch (error) {
        console.log(error);
      }
      this.isLoadingDeleted = false;
    },
    async restoreItem(item) {
      this.restoringId = item.id;
      try {
        await axios.post(`/gift_catalogs/restore/${this.encryptIt(item.id)}`);
        // Refresh both active and deleted lists
        await this.getData();
        await this.getDeletedItems();
      } catch (error) {
        console.log(error);
      }
      this.restoringId = null;
    },
    async save() {
      try {
        this.isLoading = true;
        if (this.$route.params.positionId) {
          this.form.position_id = this.decryptIt(this.$route.params.positionId);
        } else {
          this.form.position_id = "";
        }

        let formData = new FormData();
        formData.append("position_id", this.form.position_id);

        this.form.giftCatalog.forEach((level, index) => {
          if (level.id) formData.append(`giftCatalog[${index}][id]`, level.id);
          formData.append(`giftCatalog[${index}][name]`, level.name || '');
          formData.append(`giftCatalog[${index}][points]`, level.points != null ? Number(level.points) : '');
          formData.append(`giftCatalog[${index}][sort_order]`, index);
          if (level.amount != null && level.amount !== '') formData.append(`giftCatalog[${index}][amount]`, Number(level.amount));
          if (level.hsn_code) formData.append(`giftCatalog[${index}][hsn_code]`, level.hsn_code);
          if (level.width) formData.append(`giftCatalog[${index}][width]`, level.width);
          if (level.height) formData.append(`giftCatalog[${index}][height]`, level.height);
          if (level.length) formData.append(`giftCatalog[${index}][length]`, level.length);
          if (level.weight) formData.append(`giftCatalog[${index}][weight]`, level.weight);
          if (level.image_path instanceof File) {
            formData.append(`giftCatalog[${index}][image_path]`, level.image_path);
          }
        });

        await axios.post("/gift_catalogs", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await this.saveUserTimestamp(null, this.form);
        this.isLoading = false;
        this.$router.back();
      } catch (e) {
        console.error(e);
        if (e.response && e.response.data && e.response.data.errors) {
          this.formErrors = e.response.data.errors;
          const messages = Object.values(e.response.data.errors).flat();
          this.$toast.error(messages.join(', '));
        } else {
          this.$toast.error(e.response && e.response.data && e.response.data.message ? e.response.data.message : 'Failed to save changes');
        }
        this.isLoading = false;
      }
    },
    async saveUserTimestamp() {
      try {
        let form = {
          from_path: `/gift_catalogs/${this.form.id}`,
          from_name: "GiftCatalogIdPage",
          user_id: this.user.id,
          old_json: JSON.stringify(this.old_json),
          new_json: JSON.stringify(this.form),
        };
        await axios.post(`/user_timestamps`, form);
        this.$router.back();
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>
